import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import * as Ably from 'ably';
import { AblyProvider, ChannelProvider } from 'ably/react';
import { toast } from 'react-toastify';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  extractSessionIdentifierFromClientId,
  extractWebinarIdFromClientId,
} from '@/features/live/ablyClientId';
import { ablyPrivateChatChannelName } from '@/features/live/ablySessionChannels';
import { fetchAdminChatToken, fetchPublicChatToken } from '@/features/live/liveThunks';
import {
  adminLiveRouteKey,
  isChatTokenForTarget,
  isSessionNotFoundError,
  ownerLookupKey,
  pickLookupCandidates,
  shouldLookUpOwnerWorkspace,
} from '@/lib/adminLiveWorkspaceLookup';
import {
  classifyStaffAccessLinkError,
  shouldAnnounceWorkspaceSwitch,
  staffAccessLinkErrorKey,
  staffLinkGate,
  staffLinkTargetKey,
  toAdminChatRole,
} from '@/lib/staffAccessLink';
import type { StaffLinkState } from '@/lib/staffAccessLink';
import { getStoredWorkspaceId, setCurrentWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchUserWorkspaces } from '@/features/workspace/workspaceThunks';
import { liveService } from '@/services/live.service';
import SplashScreen from '@/components/common/SplashScreen';
import { useWebinarT } from '@/hooks/useWebinarT';
import i18n from '@/i18n';

function jwtExpiryMs(jwt: string): number | undefined {
  try {
    const parts = jwt.split('.');
    if (parts.length < 2) {
      return undefined;
    }
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) {
      b64 += '='.repeat(4 - pad);
    }
    const payload = JSON.parse(atob(b64)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

const FIRST_CONNECT_JITTER_MS = 3000;

// Jamais en production : le log expose le contenu des messages (dont les DM).
const ABLY_DEBUG_LOG =
  import.meta.env.VITE_ENVIRONMENT !== 'production' && import.meta.env.MODE !== 'production';

function chatTokenToAblyTokenDetails(chatToken: {
  token: string;
  clientId: string;
}): Ably.TokenDetails {
  const expires = jwtExpiryMs(chatToken.token);
  return {
    token: chatToken.token,
    clientId: chatToken.clientId,
    ...(expires != null ? { expires } : {}),
  } as Ably.TokenDetails;
}

const ChannelNameContext = createContext<string | null>(null);
const AdminChatRoleContext = createContext<string | null>(null);

export function useChannelName(): string | null {
  return useContext(ChannelNameContext);
}

export function useAdminChatRole(): string | null {
  return useContext(AdminChatRoleContext);
}

export default function AblyAuth({
  children,
  admin,
}: {
  children: React.ReactNode;
  admin?: boolean;
}) {
  const { t } = useWebinarT('webinars');
  const {
    webinarId: webinarIdParam,
    sessionId: sessionIdParam,
    sessionIdentifier: sessionIdentifierParam,
    staffLinkId: staffLinkIdParam,
  } = useParams<{
    webinarId: string;
    sessionId: string;
    sessionIdentifier: string;
    staffLinkId: string;
  }>();
  const [searchParams] = useSearchParams();
  const webinarId = webinarIdParam ? parseInt(webinarIdParam, 10) : null;
  const sessionId = sessionIdParam ? parseInt(sessionIdParam, 10) : null;
  const sessionIdentifier = sessionIdentifierParam;
  const token = searchParams.get('token');
  const staffLinkId = staffLinkIdParam ?? null;

  const currentWorkspace = useAppSelector((s: RootState) => s.workspace.currentWorkspace);
  const workspacesList = useAppSelector((s: RootState) => s.workspace.workspacesList);
  const {
    adminChatToken,
    adminChatTokenTarget,
    adminChatRole,
    adminChatTokenLoading,
    adminChatTokenError,
    publicChatToken,
    publicChatTokenLoading,
    publicChatTokenError,
  } = useAppSelector((s: RootState) => s.live);
  const dispatch = useAppDispatch();
  const [isTryingWorkspaceFallback, setIsTryingWorkspaceFallback] = useState(false);
  const [workspaceLookupDoneKey, setWorkspaceLookupDoneKey] = useState<string | null>(null);
  const workspaceLookupKeyRef = useRef<string | null>(null);
  const [jitterCompleteKey, setJitterCompleteKey] = useState<string | null>(null);
  const [staffLinkState, setStaffLinkState] = useState<StaffLinkState | null>(null);
  const staffLinkRequestedRef = useRef<string | null>(null);
  const staffWorkspaceSwitchRef = useRef<string | null>(null);

  // Route réellement affichée, relue après chaque `await` : une opération partie pour la régie
  // précédente ne doit rien écrire, et rien ne l'annule puisque le composant n'est pas remonté.
  const currentRouteKey = adminLiveRouteKey(webinarId, sessionId, staffLinkId);
  const currentRouteKeyRef = useRef<string | null>(currentRouteKey);
  useEffect(() => {
    currentRouteKeyRef.current = currentRouteKey;
  });

  // Le lien personnel désigne son espace de travail et son rôle : on les tient de lui, pas du contexte.
  const staffLinkTarget = staffLinkTargetKey(webinarId, sessionId, staffLinkId);
  const staffLink = staffLinkGate(staffLinkTarget, staffLinkState);
  const staffLinkResolution = staffLink.status === 'resolved' ? staffLink.resolution : null;
  const staffLinkPending = staffLink.status === 'pending';
  const staffLinkWorkspaceReady =
    staffLink.status === 'absent' ||
    (!!staffLinkResolution && staffLinkResolution.workspaceId === currentWorkspace?.id);

  const authParamsRef = useRef({
    admin: false as boolean,
    currentWorkspaceId: null as number | null,
    webinarId: null as number | null,
    sessionId: null as number | null,
    sessionIdentifier: null as string | null,
    token: null as string | null,
  });
  authParamsRef.current = {
    admin: !!admin,
    currentWorkspaceId: currentWorkspace?.id ?? null,
    webinarId,
    sessionId,
    sessionIdentifier: sessionIdentifier ?? null,
    token: token ?? null,
  };

  // Résolution du lien personnel : elle donne l'espace de travail et le rôle avant tout appel de jeton.
  useEffect(() => {
    if (!admin || !staffLinkId || !webinarId || !sessionId || !staffLinkTarget) {
      return;
    }
    if (staffLinkRequestedRef.current === staffLinkTarget) {
      return;
    }
    staffLinkRequestedRef.current = staffLinkTarget;
    const requestedTarget = staffLinkTarget;

    const resolveStaffLink = async () => {
      try {
        const resolution = await liveService.resolveStaffAccessLink(
          webinarId,
          sessionId,
          staffLinkId,
        );
        // Une navigation A → B laisse la demande de A en vol : sa réponse ne concerne plus la route.
        if (staffLinkRequestedRef.current !== requestedTarget) {
          return;
        }
        setStaffLinkState({ key: requestedTarget, resolution, failure: null });
      } catch (error) {
        if (staffLinkRequestedRef.current !== requestedTarget) {
          return;
        }
        setStaffLinkState({
          key: requestedTarget,
          resolution: null,
          failure: classifyStaffAccessLinkError(error),
        });
      }
    };

    void resolveStaffLink();
  }, [admin, staffLinkId, webinarId, sessionId, staffLinkTarget]);

  // Bascule directe vers l'espace du lien : aucun balayage n'est nécessaire, il est nommé.
  useEffect(() => {
    if (!staffLinkResolution || !currentWorkspace?.id || !staffLinkTarget) {
      return;
    }
    if (currentWorkspace.id === staffLinkResolution.workspaceId) {
      return;
    }
    if (staffWorkspaceSwitchRef.current === staffLinkTarget) {
      return;
    }
    staffWorkspaceSwitchRef.current = staffLinkTarget;
    const switchedTarget = staffLinkTarget;
    const startedForRoute = currentRouteKeyRef.current;
    const previousWorkspaceName = currentWorkspace.name;
    // Lu avant la bascule : `setCurrentWorkspace` réécrit aussitôt l'espace mémorisé.
    const announceSwitch = shouldAnnounceWorkspaceSwitch({
      previousWorkspaceId: currentWorkspace.id,
      targetWorkspaceId: staffLinkResolution.workspaceId,
      storedWorkspaceId: getStoredWorkspaceId(),
    });

    const switchToLinkWorkspace = async () => {
      let workspaces = workspacesList;
      if (!workspaces?.length) {
        const result = await dispatch(fetchUserWorkspaces({ page: 0 }));
        if (fetchUserWorkspaces.fulfilled.match(result)) {
          workspaces = result.payload.content ?? [];
        }
      }
      // L'utilisateur a pu partir pendant le chargement : plus rien de cette route ne doit écrire.
      if (currentRouteKeyRef.current !== startedForRoute) {
        return;
      }
      const targetWorkspace = workspaces?.find((w) => w.id === staffLinkResolution.workspaceId);
      if (!targetWorkspace) {
        if (staffLinkRequestedRef.current === switchedTarget) {
          setStaffLinkState({
            key: switchedTarget,
            resolution: null,
            failure: { kind: 'accessDenied' },
          });
        }
        return;
      }
      dispatch(
        setCurrentWorkspace({
          id: targetWorkspace.id,
          name: targetWorkspace.name,
          visualIdentity: targetWorkspace.visualIdentity ?? null,
        }),
      );
      // Quitter un espace choisi change l'espace actif bien au-delà de cette page : il faut le dire.
      if (announceSwitch) {
        toast.info(
          i18n.t('webinars:staffLink.workspaceSwitched', {
            from: previousWorkspaceName,
            to: targetWorkspace.name,
          }),
          { autoClose: 8000 },
        );
      }
    };

    void switchToLinkWorkspace();
  }, [
    staffLinkResolution,
    staffLinkTarget,
    currentWorkspace?.id,
    currentWorkspace?.name,
    workspacesList,
    dispatch,
  ]);

  useEffect(() => {
    if (admin) {
      if (!currentWorkspace?.id || !webinarId || !sessionId) {
        return;
      }
      // Sur un lien personnel, demander le jeton avant la bascule ne produirait qu'un 404.
      if (!staffLinkWorkspaceReady) {
        return;
      }
      dispatch(fetchAdminChatToken({ workspaceId: currentWorkspace.id, webinarId, sessionId }));
    } else {
      if (!webinarId || !sessionIdentifier || !token) {
        return;
      }
      dispatch(
        fetchPublicChatToken({
          webinarId,
          sessionIdentifier,
          authorization: token,
        }),
      );
    }
  }, [
    admin,
    dispatch,
    currentWorkspace?.id,
    webinarId,
    sessionId,
    sessionIdentifier,
    token,
    staffLinkWorkspaceReady,
  ]);

  const isAdminChatToken404 = isSessionNotFoundError(adminChatTokenError);

  useEffect(() => {
    if (
      !shouldLookUpOwnerWorkspace({
        admin: !!admin,
        webinarId,
        sessionId,
        currentWorkspaceId: currentWorkspace?.id ?? null,
        tokenLoading: adminChatTokenLoading,
        tokenError: adminChatTokenError,
        attemptedKey: workspaceLookupKeyRef.current,
      }) ||
      !webinarId ||
      !sessionId ||
      !currentWorkspace?.id ||
      // Le lien personnel a déjà désigné le bon espace : un 404 ici est un vrai 404.
      staffLinkId
    ) {
      return;
    }

    const lookupKey = ownerLookupKey(webinarId, sessionId);
    workspaceLookupKeyRef.current = lookupKey;
    const currentWorkspaceId = currentWorkspace.id;
    const previousWorkspaceName = currentWorkspace.name;
    // Lu avant la bascule : `setCurrentWorkspace` réécrit aussitôt l'espace mémorisé.
    const storedWorkspaceId = getStoredWorkspaceId();
    const startedForRoute = currentRouteKeyRef.current;

    const tryOtherWorkspaces = async () => {
      let workspaces = workspacesList;
      if (!workspaces?.length) {
        const result = await dispatch(fetchUserWorkspaces({ page: 0 }));
        if (fetchUserWorkspaces.fulfilled.match(result)) {
          workspaces = result.payload.content ?? [];
        } else {
          setIsTryingWorkspaceFallback(false);
          setWorkspaceLookupDoneKey(lookupKey);
          return;
        }
      }

      setIsTryingWorkspaceFallback(true);

      for (const ws of pickLookupCandidates(workspaces, currentWorkspaceId)) {
        try {
          await liveService.getAdminChatToken(ws.id, webinarId, sessionId);
          // Le balayage survit à la navigation et même au démontage : sans cette vérification, il
          // bascule l'espace actif alors que l'utilisateur est déjà ailleurs.
          if (currentRouteKeyRef.current !== startedForRoute) {
            return;
          }
          dispatch(
            setCurrentWorkspace({
              ...ws,
              socials: ws.socials ?? [],
              visualIdentity: ws.visualIdentity ?? null,
            }),
          );
          // Quitter un espace choisi change l'espace actif bien au-delà de cette page : il faut le
          // dire. Après une connexion en revanche, l'espace actif n'est qu'un défaut.
          if (
            shouldAnnounceWorkspaceSwitch({
              previousWorkspaceId: currentWorkspaceId,
              targetWorkspaceId: ws.id,
              storedWorkspaceId,
            })
          ) {
            toast.info(
              i18n.t('webinars:staffLink.workspaceSwitched', {
                from: previousWorkspaceName,
                to: ws.name,
              }),
              { autoClose: 8000 },
            );
          }
          break;
        } catch {
          continue;
        }
      }
      setIsTryingWorkspaceFallback(false);
      setWorkspaceLookupDoneKey(lookupKey);
    };

    void tryOtherWorkspaces();
  }, [
    admin,
    webinarId,
    sessionId,
    adminChatTokenLoading,
    adminChatTokenError,
    currentWorkspace?.id,
    currentWorkspace?.name,
    workspacesList,
    dispatch,
    staffLinkId,
  ]);

  // La route de régie est la même pour toutes les sessions : sans cette garde, une navigation
  // interne réutilise le jeton de la régie précédente, y compris quand la nouvelle est refusée.
  const adminTokenMatchesTarget = isChatTokenForTarget(adminChatTokenTarget, {
    workspaceId: currentWorkspace?.id ?? null,
    webinarId,
    sessionId,
  });

  const chatToken = admin ? (adminTokenMatchesTarget ? adminChatToken : null) : publicChatToken;
  const isLoading = admin ? adminChatTokenLoading : publicChatTokenLoading;

  const ablyClientKey = useMemo(() => {
    if (!chatToken?.token || !chatToken?.clientId) {
      return null;
    }
    return `${chatToken.clientId}:${chatToken.token}`;
  }, [chatToken?.token, chatToken?.clientId]);

  const pastInitialJitter = admin || (ablyClientKey != null && jitterCompleteKey === ablyClientKey);

  const realtimeClient = useMemo(() => {
    if (!chatToken?.token || !chatToken?.clientId) {
      return null;
    }

    const reuseTokenBefore = Date.now() + 250;
    const initialToken = chatToken.token;

    const authCallback = (
      _data: Ably.TokenParams,
      callback: (
        error: Ably.ErrorInfo | string | null,
        tokenOrDetails: Ably.TokenDetails | Ably.TokenRequest | string | null,
      ) => void,
    ) => {
      if (Date.now() < reuseTokenBefore) {
        callback(null, initialToken);
        return;
      }

      const params = authParamsRef.current;
      const fetchToken = async () => {
        if (params.admin) {
          if (!params.currentWorkspaceId || !params.webinarId || !params.sessionId) {
            callback('Missing params for admin token refresh', null);
            return;
          }
          const newToken = await liveService.getAdminChatToken(
            params.currentWorkspaceId,
            params.webinarId,
            params.sessionId,
          );
          callback(null, newToken.token);
        } else {
          if (!params.webinarId || !params.sessionIdentifier || !params.token) {
            callback('Missing params for public token refresh', null);
            return;
          }
          const newToken = await liveService.getPublicChatToken(
            params.webinarId,
            params.sessionIdentifier,
            params.token,
          );
          callback(null, newToken.token);
        }
      };
      void fetchToken().catch((err) =>
        callback(err instanceof Error ? err.message : String(err), null),
      );
    };

    return new Ably.Realtime({
      tokenDetails: chatTokenToAblyTokenDetails(chatToken),
      authCallback,
      ...(admin ? {} : { autoConnect: false }),
    });
  }, [admin, chatToken?.token, chatToken?.clientId]);

  useEffect(() => {
    if (!realtimeClient || !ablyClientKey || admin) {
      return;
    }
    const delayMs = Math.random() * FIRST_CONNECT_JITTER_MS;
    const id = window.setTimeout(() => {
      realtimeClient.connection.connect();
      setJitterCompleteKey(ablyClientKey);
    }, delayMs);
    return () => window.clearTimeout(id);
  }, [admin, realtimeClient, ablyClientKey]);

  const channelName = useMemo(() => {
    if (!chatToken?.clientId) {
      return null;
    }

    const extractedSessionIdentifier = extractSessionIdentifierFromClientId(chatToken.clientId);
    const extractedWebinarId = extractWebinarIdFromClientId(chatToken.clientId);

    if (!extractedSessionIdentifier || !extractedWebinarId) {
      return null;
    }

    return `webinar-${extractedWebinarId}-session-${extractedSessionIdentifier}`;
  }, [chatToken?.clientId]);

  useEffect(() => {
    if (!ABLY_DEBUG_LOG || !realtimeClient || !channelName) {
      return;
    }
    const role = admin ? 'admin' : 'public';
    const spy = (message: Ably.Message) =>
      console.log(`[Ably:${role}]`, message.name, message.data, message);
    const staffChannelName = ablyPrivateChatChannelName(channelName);
    const channels = [channelName, ...(staffChannelName ? [staffChannelName] : [])].map((name) =>
      realtimeClient.channels.get(name),
    );
    channels.forEach((channel) => {
      // Le canal -staff n'est pas ouvert à tous les rôles : on ignore l'échec d'attach.
      void channel.subscribe(spy).catch(() => {});
    });
    return () => channels.forEach((channel) => channel.unsubscribe(spy));
  }, [admin, realtimeClient, channelName]);

  if (
    admin &&
    !adminChatTokenLoading &&
    adminChatTokenError === 'HTTP.ERROR.403.USER.NOT.ASSIGNED.TO.WEBINAR'
  ) {
    return <SplashScreen label={t('staffLink.notAssigned')} fullscreen />;
  }

  const isTokenUnauthorized =
    (publicChatTokenError &&
      (publicChatTokenError.includes('401') ||
        publicChatTokenError.includes('403') ||
        publicChatTokenError.includes('404') ||
        publicChatTokenError.includes('UNAUTHORIZED') ||
        publicChatTokenError.includes('NOT_FOUND') ||
        publicChatTokenError.includes('NOT.FOUND') ||
        publicChatTokenError === 'errors.notFound')) ||
    publicChatTokenError?.includes('HTTP.ERROR.401.SUBSCRIPTION.TOKEN.MISSING_OR_INVALID');

  const isTokenMissing = !!webinarId && !!sessionIdentifier && !token;

  if (isTokenMissing || (!publicChatTokenLoading && isTokenUnauthorized)) {
    return <SplashScreen label={t('waitingRoom.publicPage.invitationInvalid')} fullscreen />;
  }

  if (admin && staffLink.status === 'failed') {
    const staffLinkFailure = staffLink.failure;
    return (
      <SplashScreen
        label={t(staffAccessLinkErrorKey(staffLinkFailure), {
          email:
            staffLinkFailure.kind === 'wrongAccount' ? (staffLinkFailure.expectedEmail ?? '') : '',
        })}
        fullscreen
      />
    );
  }

  if (admin && staffLinkPending) {
    return <SplashScreen label={t('staffLink.resolving')} fullscreen />;
  }

  if (admin && isTryingWorkspaceFallback) {
    return <SplashScreen label={t('staffLink.loadingWorkspace')} fullscreen />;
  }

  // Le drapeau vaut pour une session : sans cette clé, il resterait vrai d'une régie à l'autre.
  const workspaceLookupDone =
    webinarId != null &&
    sessionId != null &&
    workspaceLookupDoneKey === ownerLookupKey(webinarId, sessionId);

  // Sur une régie déjà ouverte, un 404 passager ne doit pas remplacer le live par un message.
  if (admin && workspaceLookupDone && !adminChatTokenLoading && isAdminChatToken404 && !chatToken) {
    return <SplashScreen label={t('staffLink.otherWorkspaceOrGone')} fullscreen />;
  }

  // Un rafraîchissement de jeton ne doit pas démonter la régie déjà affichée : elle rechargerait
  // toutes ses données et redemanderait un jeton de diffusion au remontage.
  if (!realtimeClient || !channelName) {
    return isLoading ? <SplashScreen label={t('live.loading')} fullscreen /> : null;
  }

  if (!pastInitialJitter) {
    return <SplashScreen label={t('live.loading')} fullscreen />;
  }

  // Le lien personnel fait foi : il porte le rôle voulu, là où le clientId ne porte que l'affectation.
  const staffLinkRole = toAdminChatRole(staffLinkResolution?.role);
  const adminRoleValue = admin ? (staffLinkRole ?? adminChatRole) : null;

  return (
    <AblyProvider client={realtimeClient}>
      <ChannelProvider channelName={channelName}>
        <AdminChatRoleContext.Provider value={adminRoleValue}>
          <ChannelNameContext.Provider value={channelName}>{children}</ChannelNameContext.Provider>
        </AdminChatRoleContext.Provider>
      </ChannelProvider>
    </AblyProvider>
  );
}
